import { isOpenDatePlan } from '@link/share/utils';
import {
  columnPlanForDate,
  firstBookableDate,
  firstDayForAnyPlan,
  isPlanBookableOnDate
} from '../state/bookability';
import {
  autoPickDateAndTime,
  autoPickOnlyStartTime,
  landPlanOnDate,
  moveOffDates
} from '../state/planMoves';
import type { BookingStore } from './booking';

/*
 * What the selection does by itself: the moves that bring it back to
 * something bookable when data arrives or the visitor changes something.
 * Share's `useProductPage` did these in four effects watching derived state —
 * option sell-out re-pointing, open-date/lottery auto-pick, the only-start-
 * time pick, the column layout's hold — in that declaration order. They run
 * here in the same order, from every BookingStore action.
 */

// Enough rounds for every automatic move to settle: each one only fills in or
// moves to a bookable state, so a second round finds nothing to do.
const MAX_SETTLE_ROUNDS = 5;

/**
 * Bring the selection back to something bookable after any change. Runs the
 * moves in order, again until a round changes nothing — one move can open
 * work for an earlier one (a plan switch gives a new plan to auto-pick).
 */
export function settle(store: BookingStore) {
  for (let round = 0; round < MAX_SETTLE_ROUNDS; round++) {
    const before = { plan: store.activePlanId, states: store.planStates };
    moveOffOptionSoldOutDates(store);
    autoPickForActivePlan(store);
    holdColumnSelection(store);
    if (
      store.activePlanId === before.plan &&
      store.planStates === before.states
    ) {
      return;
    }
  }
}

/**
 * Plans sitting on a day the required options sold out move to their first
 * bookable day. Moving the active plan off its date is announced, and the
 * new date becomes owned.
 */
function moveOffOptionSoldOutDates(store: BookingStore) {
  const soldOut = store.optionSoldOutDates;
  if (soldOut.size === 0) return;
  const activeId = store.activePlanId;
  const activeDate = store.activePlanState?.selectedDate ?? '';
  const activeReplacement =
    activeId &&
    activeDate &&
    soldOut.has(activeDate) &&
    !store.isLottery &&
    !isOpenDatePlan(store.activePlan)
      ? firstBookableDate(store.view, activeId)
      : '';
  if (activeReplacement) {
    store.autoSwitchedFromPlanId = '';
    takeOverOwnedDate(store, activeDate, activeReplacement);
  }
  const view = store.view;
  store.planStates = moveOffDates(
    store.planStates,
    soldOut,
    planId =>
      planId === activeId && activeReplacement
        ? activeReplacement
        : firstBookableDate(view, planId),
    store.calendar.days
  );
}

/** Dates and slots the active plan picks for itself. */
function autoPickForActivePlan(store: BookingStore) {
  const plan = store.activePlan;
  if (!plan) return;
  if (isOpenDatePlan(plan) || store.isLottery) {
    store.planStates = autoPickDateAndTime(
      store.planStates,
      plan,
      store.isLottery,
      store.calendar.days
    );
  }
  store.planStates = autoPickOnlyStartTime(store.planStates, plan);
}

/**
 * The column layout: once the calendar (and any required option calendars)
 * are in, open on the first day any plan can serve; afterwards keep the
 * active plan and its date bookable as later data narrows what is offered —
 * a plan switch while the day still has stock elsewhere, a date move once
 * it has none.
 */
function holdColumnSelection(store: BookingStore) {
  const activeId = store.activePlanId;
  if (
    !store.isColumnPlanAutoSwitch ||
    store.calendar.isLoading ||
    !store.calendar.optionCalendarsReady ||
    !activeId ||
    !store.activePlan ||
    isOpenDatePlan(store.activePlan)
  ) {
    return;
  }

  if (!store.memory.columnInitialized) {
    // A date picked before the data arrived is already the visitor's.
    if (store.memory.ownedDate) {
      store.memory.columnInitialized = true;
      return;
    }
    const first = firstDayForAnyPlan(store.view);
    if (!first) return;
    store.memory.columnInitialized = true;
    // Keep the default plan when it serves the first day.
    const planId = isPlanBookableOnDate(store.view, activeId, first.date)
      ? activeId
      : first.planId;
    const planChanged = planId !== activeId;
    if (planChanged) {
      store.autoSwitchedFromPlanId = activeId;
      store.activePlanId = planId;
    }
    store.planStates = landPlanOnDate(
      store.planStates,
      planId,
      first.date,
      planChanged,
      store.calendar.days
    );
    return;
  }

  const date = store.activePlanState?.selectedDate ?? '';
  if (!date || isPlanBookableOnDate(store.view, activeId, date)) return;

  // The day still has stock, just not on this plan: hand it to the leftmost
  // plan that serves it, and say so.
  const replacementPlanId = columnPlanForDate(store.view, activeId, date);
  if (replacementPlanId) {
    store.autoSwitchedFromPlanId = activeId;
    store.activePlanId = replacementPlanId;
    store.planStates = landPlanOnDate(
      store.planStates,
      replacementPlanId,
      date,
      true,
      store.calendar.days
    );
    return;
  }

  // No plan serves the day any more — the slot judging can empty every
  // plan on it without the whole day counting as sold out. Reopen on the
  // first day the calendar still offers. Greyed days cannot be clicked, so
  // this always follows data arriving, never a deliberate pick.
  const first = firstDayForAnyPlan(store.view);
  if (!first) return;
  const planChanged = first.planId !== activeId;
  takeOverOwnedDate(store, date, first.date);
  // The date itself moved, so a notice about a plan sold out "on it" would
  // describe a date no longer on screen.
  store.autoSwitchedFromPlanId = '';
  if (planChanged) store.activePlanId = first.planId;
  store.planStates = landPlanOnDate(
    store.planStates,
    first.planId,
    first.date,
    planChanged,
    store.calendar.days
  );
}

/** Hand the owned date over to its replacement, announcing the move. */
function takeOverOwnedDate(
  store: BookingStore,
  fromDate: string,
  toDate: string
) {
  if (!fromDate || !toDate || store.memory.ownedDate !== fromDate) return;
  store.memory.ownedDate = toDate;
  store.planDateFallback = { selectedDate: fromDate, displayDate: toDate };
}
